import React, { useCallback, useContext, useState, useEffect, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI Core
import { CircularProgress } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

// Custom Components
import ModelTable from "@/ui/Table/ModelTable";
import DialogContext from "@/ui/DialogContext/DialogContext";

import modelDijelovi from "@/models/dijelovi"
import modelObjekti from "@/models/objekti"
import dijeloviService from "@/services/dijeloviService";
import dataController from "@/lib/dataController";
import LoaderContext from "@/components/LoaderContext/LoaderContext";
import OperationsContext from "@/components/OperationsContext/OperationsContext";
import UserContext from "@/components/UserContext/UserContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import { Permissions } from "@/lib/permissions";

//Types
import { IDataController, DCRecord, DCInsertResponseSuccess } from "@/@types/lib/dataController"
import { LoaderContextType } from "@/@types/context/LoaderContext";
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { DataResponse } from "@/@types/lib/dataController";
import { OperationsContextType } from "@/components/OperationsContext/OperationsContext";
import { DialogContextType } from "@/@types/ui/DialogContext";
import { Row } from "react-table";
import TableHeaderButton from "@/ui/Table/HeaderButtons/TableHeaderButton";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Geometry } from "ol/geom";
import OlSourceVector from "ol/source/Vector";
import WKT from "ol/format/WKT";

// TODO:
// subModels cleanup: they are added to the project because Table.jsx can not function without them

interface IObjectAnalysisDijeloviTableProps {
  isDisabled: boolean;
  baseRecordPath: string;
  baseRecordId: number | null;
  katId: number;
  katNumber: number | null;

  onRowSelect: (id: number, record: DCRecord) => void;
  onDataChanged: (records: DCRecord[]) => void;
  setOriginalDijelovi: (records: DCRecord[]) => void;
  editingDio: any;
  setEditingDio: (record: DCRecord | null) => void;
  handleEditRecord: Function;
  propsRevisionCounter: number;
  // onLoad?: (records: Array<DCRecord>) => void;

  measurementsSource: OlSourceVector<Geometry>;
}

const FOR_CALC = [1, 2, 3, 5, 6, 7, 8]; // namjena_ids that need to be multiplied with koef_kn to get pre_pn

const ObjectAnalysisDijeloviTable: FunctionComponent<React.PropsWithChildren<IObjectAnalysisDijeloviTableProps>> = (props) => {
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const userContext = useContext(UserContext);
  const operationsContext = useContext<OperationsContextType>(OperationsContext);
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const { t } = useTranslation();
  const {
    baseRecordPath,
    baseRecordId,
    katId,
    katNumber,
    onRowSelect,
    onDataChanged,
    setOriginalDijelovi,
    handleEditRecord,
    editingDio,
    setEditingDio,
    propsRevisionCounter,
    isDisabled,
    measurementsSource,
  } = props;

  const [originalRecords, setOriginalRecords] = useState<DCRecord[]>([]);
  const [records, setRecords] = useState<DCRecord[]>([]);
  const [editRecordId, setEditRecordId] = useState(-1);
  const [idToReset, setIdToReset] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [currKatId, setCurrKatId] = useState(-1);
  const [revisionCounter, setRevisionCounter] = useState(0);
  const [events, setEvents] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [idToDeselect, setIdToDeselect] = useState(-1);

  const endsWithSlash = baseRecordPath.lastIndexOf('/') === baseRecordPath.length - 1 ? true : false;

  const url = `${baseRecordPath}${endsWithSlash ? "" : "/"}${baseRecordId}/katovi/${currKatId}/dijelovi`
  const dc: IDataController = new dataController(modelDijelovi, url);

  const urlObjekti = `${baseRecordPath}`;
  const dcObjekti: IDataController = new dataController(modelObjekti, urlObjekti);

  useEffect(() => {

    if (baseRecordId) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents()
    }
    return () => { };
  }, [baseRecordId, katId, records, pending])

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case 'KeyQ': if (!isDisabled) { handleAdd() } break;
        case 'KeyN': if (
          baseRecordId !== null && !isDisabled && (records.length === 0 ||
            (records.length === 1 && records[0].pre_pb === 0 && records[0].namjena_id === null)) && katId !== -1
        ) {
          (records.length === 1 && records[0].pre_pb === 0 && records[0].namjena_id === null) ?
            handleAddOstalo(Number(records[0].id)) : handleAddOstalo()
        } break;
      }
    } else {
      switch (evt.code) {
        case 'Enter':
        case 'NumpadEnter':
          if (!isDisabled) {
            setTimeout(() => {
              if (editingDio.current) {
                const record = records.find(value => value.id === editingDio.current.id)

                if (record) {
                  var pre_pb_new = editingDio.current.pre_pb
                  if (editingDio.current.pre_pn !== record.pre_pn && editingDio.current.pre_pb === record.pre_pb) {
                    pre_pb_new = editingDio.current.pre_pn
                    if (FOR_CALC.includes(editingDio.current.namjena_id.value || editingDio.current.namjena_id)) {
                      pre_pb_new = (editingDio.current.pre_pn / Number(operationsContext.selectedProject?.koef_kn)).toFixed(2)
                    }
                  }
                  const newDataObj = { ...record, pre_pb: pre_pb_new, namjena_id: editingDio.current.namjena_id.value || editingDio.current.namjena_id}
                  editingDio.current = null;
                  handleEdit(newDataObj)
                  setEditRecordId(-1)
                  handleEditRecord({}, null)
                  setIdToDeselect(record.id as number)
                }
              }
            }, 600)
          } break;
      }
    }
  }

  const registerEvents = () => {
    window.addEventListener('keydown', eventRegistrationFn);
  }

  const unregisterEvents = () => {
    window.removeEventListener('keydown', eventRegistrationFn)
  };

  useEffect(() => {
    setRecords([]);
  }, [baseRecordId])

  useEffect(() => {
    if (baseRecordId !== null && katId !== -1) {
      if (katId !== currKatId) {
        setCurrKatId(katId);
        setEditRecordId(-1);
      }
    } else {
      setCurrKatId(-1);
    }
  }, [katId]);

  useEffect(() => {
    setRecords([]);
    refreshRecords();
  }, [currKatId])

  useEffect(() => {
    refreshRecords();
  }, [revisionCounter, propsRevisionCounter])

  useEffect(() => {
    if (idToReset !== -1) {
      const originalRecord = originalRecords.find(r => r.id === idToReset);
      const record = records.find(r => r.id === idToReset);
      if (originalRecord && record) {
        setRecords(recs => recs.map(r => {
          return r.id === idToReset ? { ...originalRecord } : r;
        }));
      }
      setEditingDio(null);
      setEditRecordId(-1)
      setIdToReset(-1);
    }
  }, [idToReset])

  useEffect(() => {
    if (onDataChanged && records.length) {
      onDataChanged(records);
    }
  }, [records]);

  useEffect(() => {
    if (editRecordId !== -1) {
      const eRecord = records.find((r: DCRecord) => r.id === editRecordId);

      if (eRecord) {
        setEditingDio({ ...eRecord });
      }
    } else {
      setEditingDio(null);
    }
  }, [editRecordId])

  const refreshRecords = () => {
    if (baseRecordId !== null && currKatId !== -1) {
      setLoading(true);
      dc.GetData()
        .then((resp) => {
          if (resp.success && Array.isArray(resp.data)) {
            const recs = resp.data.map((x) => {
              if (x.namjena_id && FOR_CALC.includes(Number(x.namjena_id))) {
                return { ...x, pre_pn: (Number(x.pre_pb) * Number(operationsContext.selectedProject?.koef_kn)).toFixed(2) }
              } else {
                return { ...x, pre_pn: x.pre_pb };
              }
            })
            setRecords(recs);
            setOriginalRecords(recs.map((r: DCRecord) => ({ ...r }))); // deepcopy
            setOriginalDijelovi(recs.map((r: DCRecord) => ({ ...r })));
            if (resp.data.length === 0) {
              snackbarContext.showNotification("messages.nodata_dijelovi", "warning");
            }
          };
        })
        .catch((resp) => {
          console.log(resp);
        })
        .finally(() => {
          setLoading(false)
        });
    }
  };

  const handleRowSelect = (id: number) => {
    setEditRecordId(prevId => id === prevId ? -1 : id);
  }

  const handleAdd = () => {
    const { selectedProject } = operationsContext;
    if (pending || selectedProject === null || baseRecordId === null || katId === -1) return;
    setPending(true);
    const { id: projektId } = selectedProject;

    let max = records.length === 0 ? 0 : -Infinity;
    records.forEach((r: DCRecord) => {
      max = Math.max(max, r.broj_dijela as number);
    });

    const newDio = {
      broj_dijela: max + 1,
      projekt_id: projektId,
      kat_id: katId,
    } as DCRecord;

    // loaderContext.toggleLoading(true);
    dc.InsertRecord(newDio)
      .then((resp) => {
        const { id } = resp as DCInsertResponseSuccess;
        newDio.id = id;
        newDio.objekti_id = baseRecordId;
        setRecords(recs => [...recs, newDio]);
        setOriginalDijelovi([...records, newDio]);
      })
      .catch(err => console.log(err))
      .finally(() => setPending(false));
    // .finally(() => loaderContext.toggleLoading(false));
  }

  const handleAddOstalo = (dioId?: number) => {
    const { selectedProject } = operationsContext;
    if (pending || selectedProject === null || baseRecordId === null || katId === -1) return;
    setPending(true);
    const { id: projektId } = selectedProject;
    let pre_pt = 0;

    const newDio = {
      broj_dijela: 1,
      projekt_id: projektId,
      kat_id: katId,
      namjena_id: 8
    } as DCRecord;

    dcObjekti.GetDataSingle(baseRecordId)
      .then((resp) => {
        if (resp.success && resp.data && resp.data !== null && resp.data.hasOwnProperty('pre_pt')) {
          //@ts-ignore
          pre_pt = Number(resp.data.pre_pt);
          newDio.pre_pb = (Number(pre_pt)).toFixed(2);
        };

        if (dioId) {
          newDio.id = dioId;
          handleEdit(newDio);
          setPending(false);
        } else {
          // loaderContext.toggleLoading(true);
          dc.InsertRecord(newDio)
            .then((resp) => {
              const { id } = resp as DCInsertResponseSuccess;
              newDio.id = id;
              newDio.objekti_id = baseRecordId;
              newDio.pre_pn = ((pre_pt) * Number(operationsContext.selectedProject?.koef_kn)).toFixed(2);
              setRecords(recs => [...recs, newDio]);
              setOriginalDijelovi([...records, newDio]);
            })
            .catch(err => console.log(err))
            .finally(() => setPending(false));
          // .finally(() => loaderContext.toggleLoading(false));
        }
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => {
        // loaderContext.toggleLoading(false);
      });
  }

  const handleEdit = (record: DCRecord, includeGeom?: boolean) => {
    if (record === null || katId === -1) return;
    const { id, namjena_id, pre_pb, broj_dijela } = record;
    const namjenaValue = namjena_id
      && typeof namjena_id === 'object'
      && 'value' in namjena_id
      ? namjena_id.value
      : namjena_id;

    const updateData = {
      namjena_id: namjenaValue,
      pre_pb: pre_pb ? parseFloat(pre_pb as string) : 0,
      broj_dijela: broj_dijela ? parseFloat(broj_dijela as string) : 0,
      geom: null as string | null
    }

    if (includeGeom) {
      const feature = measurementsSource.getFeatures()[0];

      if (feature) {
        const wktFormat = new WKT();
        const wkt = wktFormat.writeFeature(feature, {
          dataProjection: 'EPSG:3765',
          featureProjection: 'EPSG:3857'
        });

        updateData.geom = wkt as string;
      }
    }

    dijeloviService.partiallyUpdateDio(
      baseRecordId as number,
      katId as number,
      id as number,
      updateData
      )
      .then(resp => {
        if (resp.success) {
          setRecords((recs: DCRecord[]) =>
            recs.map((r: DCRecord) => r.id === id ? { ...record } : { ...r })
          );
        }
      })
      .catch(resp => console.log(resp))
      .finally(() => {
        setRevisionCounter(x => x + 1);
      }
      );
  }

  const [selectedNamjena, setSelectedNamjena] = useState(1)

  useEffect(() => {
    setSelectedNamjena(records.find((record) => record.id === editRecordId)?.namjena_id as number);
  }, [editRecordId])

  const handleFieldChange = (value: any, source: string, onFormFieldChangeFn: Function) => {
    switch (source) {
      case "namjena_id":
        setSelectedNamjena(value.value)
        if (onFormFieldChangeFn) {
          let koeficijent = Number(operationsContext.selectedProject?.koef_kn)
          if (!FOR_CALC.includes(value.value)) koeficijent = 1
          const pre_pb = records.find((record) => record.id === editRecordId)?.pre_pb || 0;
          const pre_pn = pre_pb ? ((pre_pb as number) * koeficijent).toFixed(2) : null;
          onFormFieldChangeFn(pre_pn, "pre_pn");
          onFormFieldChangeFn(pre_pb, "pre_pb");
          onFormFieldChangeFn(value, source);
        }
        break;
      case "pre_pn":
        if (onFormFieldChangeFn) {
          let koeficijent = Number(operationsContext.selectedProject?.koef_kn)
          if (!FOR_CALC.includes(selectedNamjena)) koeficijent = 1
          const pre_pb = value ? (value / koeficijent).toFixed(2) : null;
          onFormFieldChangeFn(pre_pb, "pre_pb");
        }
        break;
      case "pre_pb":
        if (onFormFieldChangeFn) {
          let koeficijent = Number(operationsContext.selectedProject?.koef_kn)
          if (!FOR_CALC.includes(selectedNamjena)) koeficijent = 1
          const pre_pn = value ? (value * koeficijent).toFixed(2) : null;
          onFormFieldChangeFn(pre_pn, "pre_pn");
        }
        break;
    }

    if (handleEditRecord) {
      handleEditRecord(value, source);
    }
  }

  const handleRecordChange = (record: DCRecord | null) => {
    if (record === null) return;
    setRecords(recs => recs.map((r: DCRecord) => {
      let nRecord: DCRecord | null = null;
      if (r.id === record.id) {
        nRecord = { ...record };
      }
      return r.id === record.id ? { ...record } : r;
    }));
  }

  const handleRowAction = (action: string, id: number, record?: DCRecord, row?: Row) => {
    switch (action) {
      case "cancel": {
        setIdToReset(id)

        row?.toggleRowSelected(false)
        break;
      }
      default:
        console.log("No action defined");
        break
    }
  }

  const handleDeleted = () => {
    setRevisionCounter((cnt) => cnt + 1);
  };

  const handleCloseInlineEdit = (row: any) => {
    handleRowAction("cancel", row.original['id'], row.original, row)
  }

  const isAdmin = userContext?.hasAnyPermission([Permissions.Administration]) ? true : false;

  return (
    <ModelTable
      title={t("titles.dijelovi")}
      subTitle={
        <>
          {katNumber !== null ? <span style={{ fontSize: "12px" }}>({t("titles.kat")}: {katNumber})</span> : null}
          {loading ?
            <CircularProgress disableShrink size={20} />
            : null
          }
        </>
      }
      records={records}
      viewName={isAdmin ? "admin" : "default"}
      dc={dc}
      allowSearchBar={false}
      allowSelection="one"
      allowFilter={false}
      allowExport={false}
      allowAdd={!isDisabled && katId !== -1}
      allowEdit={!isDisabled}
      allowInlineEdit={!isDisabled}
      allowInlineEditQuickly={!isDisabled}
      allowDelete={!isDisabled}
      onDeleted={handleDeleted}
      addLabel={t("buttons.new_m")}
      addTooltip="tooltips.shortcuts.add_dio"
      handleAdd={handleAdd}
      allowColumnPicker={false}
      allowInc={false}
      disableControls={!isDisabled}
      showControlSwitch={false}
      onRowAction={handleRowAction}
      onRowClick={handleRowSelect}
      initialPageSize={25}
      allowRowAction={() => { return true; }}
      trackTableState={false}
      unselectRow={true}
      customRecordCount={records.length}
      gotoFirstPageOnSelect={true}
      onRecordChange={handleEdit}
      onFieldChangeFn={handleFieldChange}
      closeInlineEdit={handleCloseInlineEdit}
      deselectRowID={idToDeselect}
      setDeselectRowID={setIdToDeselect}
      CustomAddComponent={
        baseRecordId !== null && !isDisabled && (records.length === 0
          || (records.length === 1 && records[0].pre_pb === 0 && records[0].namjena_id === null)) && katId !== -1 ?
          <Tooltip title={baseRecordId === null || isDisabled ? "" : t("tooltips.shortcuts.add_ostalo") as string}>
            <TableHeaderButton
              startIcon={<AddCircleIcon />}
              onClick={(evt) => ((records.length === 1 && records[0].pre_pb === 0 && records[0].namjena_id === null) ?
                handleAddOstalo(Number(records[0].id)) : handleAddOstalo())}
              variant="outlined"
              disabled={baseRecordId === null || isDisabled}
            >
              {t("buttons.add_ostalo")}
            </TableHeaderButton>
          </Tooltip>
          : null
      }
    // rowSelectedCustom={editRecordId}
    />
  );
}

export default ObjectAnalysisDijeloviTable;
